import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { downloadXlsx } from "@/lib/download";
import { toast } from "sonner";
import { useCurrentStudy } from "@/context/CurrentStudyContext";
import { WhatsNewBanner } from "@/components/WhatsNewBanner";
import {
  Pill, Layers, PackageX, TrendingDown, Trophy, Frown, Loader2,
  ArrowUpRight, ArrowDownRight, Minus, Gauge, FileBarChart, Globe2, Coins, FileDown, PackagePlus,
} from "lucide-react";

const fmtEur = (n) => Number(n || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 2 });
const fmtNum = (n) => Number(n || 0).toLocaleString("es-ES");
const fmtPct = (n) => `${Number(n || 0).toFixed(1)}%`;

/* ---------- Chip de variación vs mes anterior ---------- */
function DeltaChip({ value, unit = "pp", goodWhenUp = true, testid }) {
  if (value == null || !isFinite(value)) {
    return <span className="text-[10px] text-slate-400 font-semibold" data-testid={testid}>sin mes anterior</span>;
  }
  const flat = Math.abs(value) < 0.05;
  const good = goodWhenUp ? value > 0 : value < 0;
  const cls = flat
    ? "bg-slate-100 text-slate-600 border-slate-200"
    : good
      ? "bg-emerald-100 text-emerald-800 border-emerald-200"
      : "bg-rose-100 text-rose-800 border-rose-200";
  const Icon = flat ? Minus : value > 0 ? ArrowUpRight : ArrowDownRight;
  const txt = unit === "eur"
    ? fmtEur(Math.abs(value))
    : unit === "num"
      ? fmtNum(Math.round(Math.abs(value)))
      : `${Math.abs(value).toFixed(1)} pp`;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] font-bold border rounded-full px-2 py-0.5 font-mono-num ${cls}`}
      data-testid={testid}
    >
      <Icon className="h-3 w-3" strokeWidth={2.6} />
      {value > 0 ? "+" : value < 0 ? "−" : ""}{txt}
    </span>
  );
}

/* ---------- Tarjeta KPI con comparativa ---------- */
function SummaryKpi({ icon: Icon, label, value, sub, delta, deltaUnit, goodWhenUp = true, accent = "slate", href, testid }) {
  const accents = {
    emerald: "border-emerald-200 text-emerald-700",
    sky: "border-sky-200 text-sky-700",
    rose: "border-rose-200 text-rose-700",
    amber: "border-amber-200 text-amber-700",
    indigo: "border-indigo-200 text-indigo-700",
    slate: "border-slate-200 text-slate-700",
  };
  const Wrap = href ? Link : "div";
  const props = href ? { to: href } : {};
  return (
    <Wrap
      {...props}
      data-testid={testid}
      className={`block bg-white border rounded-2xl p-5 transition-all duration-200 ${accents[accent]} ${href ? "hover:shadow-lg hover:-translate-y-0.5" : ""}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">{label}</span>
        {Icon && <Icon className="h-4 w-4 opacity-70" strokeWidth={1.8} />}
      </div>
      <div className="font-display text-3xl font-extrabold tracking-tight text-slate-950 font-mono-num">{value}</div>
      <div className="mt-2 flex items-center gap-2 flex-wrap">
        <DeltaChip value={delta} unit={deltaUnit} goodWhenUp={goodWhenUp} testid={`${testid}-delta`} />
        {sub && <span className="text-xs text-slate-500">{sub}</span>}
      </div>
    </Wrap>
  );
}

function Panel({ title, icon: Icon, children, className = "", testid, action }) {
  return (
    <section className={`bg-white border border-slate-200 rounded-2xl p-5 ${className}`} data-testid={testid}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <h2 className="text-base md:text-lg font-bold text-slate-900 inline-flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-slate-500" strokeWidth={2} />}
          {title}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

export default function StudySummaryPage() {
  const { id: routeId } = useParams();
  const { current } = useCurrentStudy();
  const [dispId, setDispId] = useState(routeId || null);
  const [data, setData] = useState(null);
  const [bench, setBench] = useState(null);
  const [loading, setLoading] = useState(true);
  // Aviso tras subir el estudio más reciente (lo manda UploadPage por router state).
  const location = useLocation();
  const [remindStock, setRemindStock] = useState(!!location.state?.remindStock);

  useEffect(() => {
    if (routeId) { setDispId(routeId); return; }
    if (current?.id) { setDispId(current.id); return; }
    api.get("/dispensations").then((r) => {
      const list = r.data || [];
      if (list.length) setDispId(list[0].id);
      else setLoading(false);
    }).catch(() => setLoading(false));
  }, [routeId, current?.id]);

  useEffect(() => {
    if (!dispId) return;
    setLoading(true);
    api.get("/dashboard/executive", { params: { disp_id: dispId } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Error al cargar el resumen"))
      .finally(() => setLoading(false));
    api.get("/benchmark/provinces").then((r) => setBench(r.data)).catch(() => setBench(null));
  }, [dispId]);

  const k = data?.kpis || {};
  const tl = data?.timeline || [];
  const disp = data?.dispensation || {};

  const { prev, cur, deltaGenEur } = useMemo(() => {
    const idx = tl.findIndex((t) => t.id === dispId);
    const i = idx >= 0 ? idx : tl.findIndex((t) => t.is_current);
    const c = i >= 0 ? tl[i] : tl[tl.length - 1] || null;
    // "Mes anterior" = el mismo criterio que usa el backend para las moléculas
    // en caída: el estudio cuyo periodo termina justo antes del actual.
    const prevId = data?.molecules_falling?.prev_id;
    let p = prevId ? tl.find((t) => t.id === prevId) : null;
    if (!p && c) {
      const curEnd = c.period_end || c.period_start || "";
      const cands = tl.filter((t) => t.id !== c.id && (t.period_end || t.period_start || "") < curEnd);
      p = cands.length
        ? cands.reduce((a, b) => ((b.period_end || "") > (a.period_end || "") ? b : a))
        : null;
    }
    if (!c || !p) return { prev: null, cur: c, deltaGenEur: null };
    const cf = (c.days || 30) > 0 ? 30 / (c.days || 30) : 1;
    const pf = (p.days || 30) > 0 ? 30 / (p.days || 30) : 1;
    return { prev: p, cur: c, deltaGenEur: (c.eur_generico || 0) * cf - (p.eur_generico || 0) * pf };
  }, [tl, dispId, data?.molecules_falling?.prev_id]);

  const d = (field) => (prev && cur ? Number(cur[field] || 0) - Number(prev[field] || 0) : null);
  // Para importes/unidades comparamos proyectando ambos periodos a 30 días,
  // así no se compara un estudio de 10 días con otro de 30.
  const factor = (t) => ((t?.days || 30) > 0 ? 30 / (t.days || 30) : 1);
  const dProj = (field) =>
    prev && cur ? Number(cur[field] || 0) * factor(cur) - Number(prev[field] || 0) * factor(prev) : null;

  const gap = data?.stock_gap;
  const isStockMode = gap?.mode === "stock";
  const gapCount = isStockMode ? gap?.molecules_without_stock : gap?.solo_marca_count;
  const gapEur = isStockMode ? gap?.marca_euros_at_risk : gap?.solo_marca_euros;
  const falling = (data?.molecules_falling?.items || []).slice(0, 3);
  const emp = data?.employees;
  const sp = bench?.self_position;
  const top25 = data?.top_25_brand_summary || {};

  if (loading && !data) {
    return (
      <div className="p-10 flex items-center gap-3 text-slate-500" data-testid="summary-loading">
        <Loader2 className="h-5 w-5 animate-spin" /> Cargando resumen del estudio…
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-8 max-w-xl" data-testid="summary-empty">
        <h1 className="font-display text-2xl font-extrabold text-slate-900">Aún no hay estudios</h1>
        <p className="text-sm text-slate-500 mt-2">Sube tu primera dispensación para ver el resumen del mes.</p>
        <Link to="/upload" className="inline-block mt-4 bg-slate-900 text-white px-4 py-2 rounded-lg text-sm font-bold" data-testid="summary-go-upload">
          Subir dispensación
        </Link>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 md:p-8 lg:p-10 max-w-[1500px]" data-testid="study-summary-page">
      <WhatsNewBanner />
      {remindStock && (
        <div
          className="mb-6 rounded-2xl border-2 border-amber-400 bg-amber-50 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4"
          data-testid="stock-reminder-banner"
        >
          <PackagePlus className="h-10 w-10 text-amber-600 shrink-0" strokeWidth={2.2} />
          <div className="flex-1">
            <div className="font-display text-xl sm:text-2xl font-extrabold tracking-tight text-amber-900">
              ACUÉRDATE DE SUBIR EL STOCK
            </div>
            <p className="text-sm text-amber-900/80 mt-1">
              Acabas de subir tu estudio más reciente. Sube ahora el inventario para que las faltas,
              los pedidos y los mínimos y máximos salgan con datos de hoy.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Link
              to="/stock"
              className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-bold px-4 py-2.5 rounded-lg"
              data-testid="stock-reminder-go"
            >
              <PackagePlus className="h-4 w-4" /> Subir stock ahora
            </Link>
            <button
              onClick={() => setRemindStock(false)}
              className="text-sm font-bold text-amber-800 hover:text-amber-950 px-3 py-2.5"
              data-testid="stock-reminder-dismiss"
            >
              Más tarde
            </button>
          </div>
        </div>
      )}
      {/* Cabecera + comparativa */}
      <div className="rounded-2xl bg-slate-950 text-white p-6 md:p-8 mb-6 relative overflow-hidden" data-testid="summary-hero">
        <div className="absolute inset-0 opacity-[0.07] bg-[radial-gradient(circle_at_20%_20%,#34d399,transparent_45%),radial-gradient(circle_at_80%_10%,#38bdf8,transparent_40%)]" />
        <div className="relative">
          <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-emerald-300/90">Resumen del estudio</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mt-1" data-testid="summary-title">
            {disp.label || "Estudio"}
          </h1>
          <p className="text-slate-300 text-sm mt-2 font-mono-num">
            {disp.period_start} → {disp.period_end} · {disp.months} mes{Number(disp.months) !== 1 ? "es" : ""}
          </p>

          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white/5 border border-white/10 rounded-xl p-4" data-testid="summary-vs-prev-eur">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">€ genérico vs {prev?.label || "mes anterior"}</div>
              <div className={`font-display text-3xl font-extrabold font-mono-num mt-1 ${deltaGenEur == null ? "text-slate-400" : deltaGenEur >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                {deltaGenEur == null ? "—" : `${deltaGenEur >= 0 ? "+" : "−"}${fmtEur(Math.abs(deltaGenEur))}`}
              </div>
              <div className="text-[11px] text-slate-400 mt-1">Proyectado a 30 días para comparar de forma justa</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4" data-testid="summary-vs-prev-pp">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">Cuota genérico (€) vs anterior</div>
              <div className={`font-display text-3xl font-extrabold font-mono-num mt-1 ${d("pct_eur") == null ? "text-slate-400" : d("pct_eur") >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                {d("pct_eur") == null ? "—" : `${d("pct_eur") >= 0 ? "+" : "−"}${Math.abs(d("pct_eur")).toFixed(1)} pp`}
              </div>
              <div className="text-[11px] text-slate-400 mt-1">
                {prev ? `${fmtPct(prev.pct_eur)} → ${fmtPct(cur?.pct_eur)}` : "Sube otro estudio para comparar"}
              </div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4" data-testid="summary-loss">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">Pérdida estimada del mes</div>
              <div className="font-display text-3xl font-extrabold font-mono-num mt-1 text-amber-300">{fmtEur(top25.marca_euros || 0)}</div>
              <div className="text-[11px] text-slate-400 mt-1">
                Top 25 moléculas en marca · {fmtPct(top25.pct_over_total_marca || 0)} del total marca
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            <Link to="/cuadro-mando" className="bg-white text-slate-950 px-4 py-2 rounded-lg text-xs font-bold inline-flex items-center gap-1.5 hover:bg-emerald-300 transition-colors" data-testid="summary-go-cuadro">
              <Gauge className="h-3.5 w-3.5" /> Cuadro de mando completo
            </Link>
            <Link to={`/dispensations/${disp.id}`} className="bg-white/10 border border-white/20 text-white px-4 py-2 rounded-lg text-xs font-bold inline-flex items-center gap-1.5 hover:bg-white/20 transition-colors" data-testid="summary-go-study">
              <FileBarChart className="h-3.5 w-3.5" /> Ver estudio completo
            </Link>
            <Link to="/comparison" className="bg-white/10 border border-white/20 text-white px-4 py-2 rounded-lg text-xs font-bold inline-flex items-center gap-1.5 hover:bg-white/20 transition-colors" data-testid="summary-go-comparison">
              <TrendingDown className="h-3.5 w-3.5" /> Comparativa entre estudios
            </Link>
            <button
              onClick={() => downloadXlsx(`/dispensations/${disp.id}/summary-pdf`, `farmaclass_resumen_${(disp.label || "mes").replace(/\s+/g, "_")}.pdf`)}
              className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 px-4 py-2 rounded-lg text-xs font-bold inline-flex items-center gap-1.5 transition-colors"
              data-testid="summary-download-pdf"
            >
              <FileDown className="h-3.5 w-3.5" /> Descargar resumen en PDF
            </button>
          </div>
        </div>
      </div>

      {/* KPIs principales con comparativa */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-6" data-testid="summary-kpis">
        <SummaryKpi
          icon={Pill} accent="emerald" label="% Genérico (uds)"
          value={fmtPct(k.pct_gen_units)} delta={d("pct_units")}
          sub={`${fmtNum(k.units_total)} uds`}
          href={`/dispensations/${disp.id}?tab=overview`} testid="summary-kpi-pct-uds"
        />
        <SummaryKpi
          icon={Pill} accent="sky" label="% Genérico (€)"
          value={fmtPct(k.pct_gen_eur)} delta={d("pct_eur")}
          sub={fmtEur(k.eur_total)}
          href={`/dispensations/${disp.id}?tab=overview&sort=euros&dir=desc`} testid="summary-kpi-pct-eur"
        />
        <SummaryKpi
          icon={Coins} accent="rose" label="€ Marca total"
          value={fmtEur(k.eur_marca)} delta={dProj("eur_marca")} deltaUnit="eur" goodWhenUp={false}
          sub="vs mes anterior (30 d) · cuanto menos, mejor"
          href={`/dispensations/${disp.id}?tab=marca&sort=euros&dir=desc`} testid="summary-kpi-eur-marca"
        />
        <SummaryKpi
          icon={Coins} accent="emerald" label="€ Genérico total"
          value={fmtEur(k.eur_generico)} delta={dProj("eur_generico")} deltaUnit="eur"
          sub="vs mes anterior (30 d)"
          href={`/dispensations/${disp.id}?tab=overview`} testid="summary-kpi-eur-generico"
        />
        <SummaryKpi
          icon={Layers} accent="indigo" label="Unidades totales"
          value={fmtNum(k.units_total)} delta={dProj("units_total")} deltaUnit="num"
          sub={`${fmtNum(k.total_gh)} G.H.`}
          href={`/dispensations/${disp.id}?tab=molecules`} testid="summary-kpi-units"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {/* Moléculas que te faltan */}
        <Panel
          title={isStockMode ? "Moléculas que te faltan (sin stock de genérico)" : "Moléculas en SOLO MARCA"}
          icon={PackageX}
          testid="summary-gap-panel"
          action={
            <Link to={isStockMode ? "/sin-stock-genericos" : `/dispensations/${disp.id}?tab=marca`} className="text-xs font-bold text-sky-700 hover:underline" data-testid="summary-gap-link">
              Ver listado →
            </Link>
          }
        >
          {!gap ? (
            <p className="text-sm text-slate-500">Sube tu stock para detectar las moléculas que te faltan.</p>
          ) : (
            <>
              <div className="flex items-end gap-6">
                <div>
                  <div className="font-display text-4xl font-extrabold text-amber-700 font-mono-num">{fmtNum(gapCount)}</div>
                  <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mt-1">moléculas</div>
                </div>
                <div>
                  <div className="font-display text-4xl font-extrabold text-rose-700 font-mono-num" data-testid="summary-gap-eur">{fmtEur(gapEur)}</div>
                  <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mt-1">en € de marca en juego</div>
                </div>
              </div>
              <ul className="mt-4 divide-y divide-slate-100">
                {(gap.top || []).slice(0, 3).map((m) => (
                  <li key={m.gh} className="py-2 flex items-center justify-between gap-3 text-sm" data-testid={`summary-gap-row-${m.gh}`}>
                    <span className="truncate text-slate-800" title={m.gh}>{m.gh}</span>
                    <span className="font-mono-num font-bold text-slate-900 whitespace-nowrap">{fmtEur(m.marca_euros)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </Panel>

        {/* Posición en el ranking */}
        <Panel title="Tu posición frente a otras farmacias" icon={Globe2} testid="summary-rank-panel"
          action={<Link to="/benchmark" className="text-xs font-bold text-sky-700 hover:underline" data-testid="summary-rank-link">Ver benchmark →</Link>}
        >
          {!sp ? (
            <p className="text-sm text-slate-500">Todavía no hay datos suficientes para situarte en el ranking.</p>
          ) : (
            <>
              <p className="text-xs text-slate-500 mb-3">
                Tu % genérico (€): <strong className="text-slate-900 font-mono-num">{fmtPct(sp.self_generic_pct)}</strong>
                {sp.ccaa_avg_generic_pct != null && <> · Media {sp.ccaa}: <span className="font-mono-num">{fmtPct(sp.ccaa_avg_generic_pct)}</span></>}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="summary-rank-ccaa">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">{sp.ccaa || "Tu CCAA"}</div>
                  <div className="font-display text-3xl font-extrabold font-mono-num text-slate-900 mt-1">#{sp.ccaa_rank || "—"}</div>
                  <div className="text-[11px] text-slate-500 font-mono-num">de {sp.ccaa_studies || "—"} · percentil {sp.ccaa_percentile != null ? `${sp.ccaa_percentile.toFixed(1)}%` : "—"}</div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="summary-rank-national">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Nacional</div>
                  <div className="font-display text-3xl font-extrabold font-mono-num text-slate-900 mt-1">#{sp.national_rank || "—"}</div>
                  <div className="text-[11px] text-slate-500 font-mono-num">de {sp.national_universe || "—"} · percentil {sp.national_percentile != null ? `${sp.national_percentile.toFixed(1)}%` : "—"}</div>
                </div>
              </div>
            </>
          )}
        </Panel>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top 3 moléculas en caída */}
        <Panel
          title={`Top 3 moléculas en caída vs ${data?.molecules_falling?.prev_label || "el mes anterior"}`}
          icon={TrendingDown}
          testid="summary-falling-panel"
          action={<Link to="/cuadro-mando" className="text-xs font-bold text-rose-700 hover:underline" data-testid="summary-falling-link">Ver todas →</Link>}
        >
          {falling.length === 0 ? (
            <p className="text-sm text-slate-500">Ninguna molécula ha caído más de 10 puntos respecto al estudio anterior. Buen trabajo.</p>
          ) : (
            <table className="w-full text-sm" data-testid="summary-falling-table">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="text-left py-2">Molécula</th>
                  <th className="text-right py-2 hidden sm:table-cell">Antes</th>
                  <th className="text-right py-2">Ahora</th>
                  <th className="text-right py-2">Caída</th>
                  <th className="text-right py-2">Marca €</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {falling.map((m) => (
                  <tr key={m.gh} data-testid={`summary-falling-row-${m.gh}`}>
                    <td className="py-2 max-w-[150px] sm:max-w-[240px]"><span className="block truncate text-slate-800" title={m.gh}>{m.gh}</span></td>
                    <td className="py-2 text-right font-mono-num text-slate-500 hidden sm:table-cell">{fmtPct(m.pct_prev)}</td>
                    <td className="py-2 text-right font-mono-num font-semibold">{fmtPct(m.pct_now)}</td>
                    <td className="py-2 text-right font-mono-num font-bold text-rose-700">{Number(m.delta_pp).toFixed(1)} pp</td>
                    <td className="py-2 text-right font-mono-num whitespace-nowrap">{fmtEur(m.marca_euros)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>

        {/* Empleado top */}
        <Panel title="Empleado del mes" icon={Trophy} testid="summary-employee-panel"
          action={<Link to="/empleados/analisis" className="text-xs font-bold text-sky-700 hover:underline" data-testid="summary-employee-link">Ver análisis →</Link>}
        >
          {!emp?.best ? (
            <p className="text-sm text-slate-500">Este estudio no trae desglose por empleado.</p>
          ) : (
            <div className="space-y-3">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 flex items-center justify-between gap-3" data-testid="summary-employee-best">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 inline-flex items-center gap-1.5">
                    <Trophy className="h-3.5 w-3.5 text-emerald-600" /> Mejor empleado
                  </div>
                  <div className="font-semibold text-slate-900 truncate">{emp.best.name}</div>
                  <div className="text-[11px] text-slate-500 font-mono-num mt-0.5">
                    {fmtPct(emp.best.pct_units)} uds · {fmtNum(emp.best.units_total)} uds · {fmtEur(emp.best.eur_total)}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display text-2xl sm:text-3xl font-extrabold font-mono-num text-emerald-700">{fmtPct(emp.best.pct_eur)}</div>
                  <div className="text-[10px] text-slate-500 font-mono-num">% gen €</div>
                </div>
              </div>
              {emp.worst && (
                <div className="rounded-xl border border-rose-200 bg-rose-50/50 p-3 flex items-center justify-between gap-3" data-testid="summary-employee-worst">
                  <div className="min-w-0 inline-flex items-center gap-2">
                    <Frown className="h-3.5 w-3.5 text-rose-600 shrink-0" />
                    <span className="text-sm text-slate-800 truncate">A mejorar: <strong>{emp.worst.name}</strong></span>
                  </div>
                  <span className="font-mono-num font-bold text-rose-700">{fmtPct(emp.worst.pct_eur)}</span>
                </div>
              )}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
